import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Mail, Search, Loader2, RefreshCw, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, AlertTriangle, Building2, Globe, Send, MailOpen,
  MousePointerClick, Undo2, Clock, Ban, Eye, Trash2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const STATUS_META = {
  sent: { label: 'Aceite', className: 'bg-sky-100 text-sky-700 border-sky-500/30', icon: Send },
  processed: { label: 'Processado', className: 'bg-sky-100 text-sky-700 border-sky-500/30', icon: Send },
  delivered: { label: 'Entregue', className: 'bg-emerald-100 text-emerald-700 border-emerald-500/30', icon: CheckCircle2 },
  open: { label: 'Aberto', className: 'bg-indigo-100 text-indigo-700 border-indigo-500/30', icon: MailOpen },
  click: { label: 'Clique', className: 'bg-violet-100 text-violet-700 border-violet-500/30', icon: MousePointerClick },
  deferred: { label: 'Adiado', className: 'bg-amber-100 text-amber-700 border-amber-500/30', icon: Clock },
  bounce: { label: 'Devolvido', className: 'bg-red-100 text-red-700 border-red-500/30', icon: Undo2 },
  dropped: { label: 'Rejeitado', className: 'bg-red-100 text-red-700 border-red-500/30', icon: Ban },
  spamreport: { label: 'Spam', className: 'bg-red-100 text-red-700 border-red-500/30', icon: AlertTriangle },
  failed: { label: 'Falhou', className: 'bg-red-100 text-red-700 border-red-500/30', icon: XCircle },
  not_configured: { label: 'Sem chave', className: 'bg-slate-100 text-slate-700 border-slate-500/30', icon: AlertTriangle },
};
const statusMeta = (s) => STATUS_META[s] || STATUS_META.not_configured;

const EmailLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ sent: 0, failed: 0, not_configured: 0 });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total: 0 });
  const [filters, setFilters] = useState({ scope: 'all', status: 'all', search: '' });
  const [searchInput, setSearchInput] = useState('');
  const [detailLog, setDetailLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = searchParams.get('tenant_id') || '';
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'single'|'bulk', id?, count? }
  const [deleting, setDeleting] = useState(false);

  const fetchLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (filters.scope !== 'all') params.scope = filters.scope;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (tenantId) params.tenant_id = tenantId;
      const res = await api.get('/super-admin/email-logs', { params });
      setLogs(res.data.logs || []);
      setSelectedIds([]);
      setStats(res.data.stats || { sent: 0, failed: 0, not_configured: 0 });
      setPagination({ page: res.data.page, total_pages: res.data.total_pages, total: res.data.total });
    } catch (err) {
      console.error('Error fetching email logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, tenantId]);

  useEffect(() => { fetchLogs(1); }, [fetchLogs]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => prev.length === logs.length ? [] : logs.map(l => l.id));
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'single') {
        await api.delete(`/super-admin/email-logs/${deleteTarget.id}`);
        toast.success('Registo de email eliminado');
      } else {
        const res = await api.post('/super-admin/email-logs/bulk-delete', { ids: selectedIds });
        toast.success(res.data.message);
      }
      setDeleteTarget(null);
      await fetchLogs(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao eliminar registo(s)');
    } finally {
      setDeleting(false);
    }
  };

  const openDetail = async (log) => {
    setDetailLog(log);
    setShowRawHtml(false);
    setDetailLoading(true);
    try {
      const res = await api.get(`/super-admin/email-logs/${log.id}`);
      setDetailLog(res.data);
    } catch (err) {
      toast.error('Erro ao carregar o conteúdo do email');
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (s) => {
    if (!s) return '-';
    return new Date(s).toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const StatCard = ({ label, value, tone, Icon }) => (
    <Card className="bg-white border-slate-200">
      <CardContent className="pt-6 flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className={`text-3xl font-bold ${tone}`}>{value}</p>
        </div>
        <Icon className={`w-8 h-8 ${tone}`} />
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="email-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-indigo-700" />
            Registo de Emails
          </h1>
          <p className="text-slate-500 mt-1">
            Todos os emails da plataforma — sistema e de cada cliente ({pagination.total} registos)
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-50"
              onClick={() => setDeleteTarget({ type: 'bulk', count: selectedIds.length })}
              data-testid="bulk-delete-email-logs-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Eliminar selecionados ({selectedIds.length})
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            onClick={() => fetchLogs(pagination.page)}
            data-testid="refresh-email-logs"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Enviados" value={stats.sent} tone="text-emerald-600" Icon={CheckCircle2} />
        <StatCard label="Falhados" value={stats.failed} tone="text-red-600" Icon={XCircle} />
        <StatCard label="Sem chave" value={stats.not_configured} tone="text-slate-500" Icon={AlertTriangle} />
      </div>

      {/* Tenant filter banner */}
      {tenantId && (
        <div className="flex items-center justify-between rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3" data-testid="tenant-filter-banner">
          <div className="flex items-center gap-2 text-sm text-indigo-800">
            <Building2 className="w-4 h-4" />
            A mostrar apenas o canal: <span className="font-semibold">{logs[0]?.tenant_name || 'cliente selecionado'}</span>
          </div>
          <Button variant="ghost" size="sm" className="text-indigo-700 hover:bg-indigo-100"
            onClick={() => { const p = new URLSearchParams(searchParams); p.delete('tenant_id'); setSearchParams(p); }}
            data-testid="clear-tenant-filter">
            Limpar
          </Button>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="w-48">
              <Select value={filters.scope} onValueChange={(v) => setFilters(p => ({ ...p, scope: v }))}>
                <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="scope-filter">
                  <SelectValue placeholder="Âmbito" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os âmbitos</SelectItem>
                  <SelectItem value="system">Sistema (Plataforma)</SelectItem>
                  <SelectItem value="tenant">Clientes (Tenants)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select value={filters.status} onValueChange={(v) => setFilters(p => ({ ...p, status: v }))}>
                <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="status-filter">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os estados</SelectItem>
                  <SelectItem value="sent">Aceite</SelectItem>
                  <SelectItem value="delivered">Entregue</SelectItem>
                  <SelectItem value="open">Aberto</SelectItem>
                  <SelectItem value="click">Clique</SelectItem>
                  <SelectItem value="deferred">Adiado</SelectItem>
                  <SelectItem value="bounce">Devolvido</SelectItem>
                  <SelectItem value="dropped">Rejeitado</SelectItem>
                  <SelectItem value="spamreport">Spam</SelectItem>
                  <SelectItem value="failed">Falhou</SelectItem>
                  <SelectItem value="not_configured">Sem chave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <form
              className="flex-1 min-w-[220px] flex gap-2"
              onSubmit={(e) => { e.preventDefault(); setFilters(p => ({ ...p, search: searchInput })); }}
            >
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Procurar por destinatário, assunto ou cliente..."
                  className="bg-white border-slate-300 text-slate-900 pl-9"
                  data-testid="email-search-input"
                />
              </div>
              <Button type="submit" className="bg-indigo-700 text-white hover:bg-indigo-800">Procurar</Button>
            </form>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-700" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Mail className="w-10 h-10 mb-3 opacity-50" />
              <p>Nenhum email registado ainda</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="p-4 font-medium w-10">
                      <Checkbox
                        checked={logs.length > 0 && selectedIds.length === logs.length}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-email-logs"
                      />
                    </th>
                    <th className="p-4 font-medium">Data</th>
                    <th className="p-4 font-medium">Âmbito</th>
                    <th className="p-4 font-medium">Destinatário</th>
                    <th className="p-4 font-medium">Assunto</th>
                    <th className="p-4 font-medium">Estado</th>
                    <th className="p-4 font-medium text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const meta = statusMeta(log.status);
                    const StatusIcon = meta.icon;
                    return (
                      <tr key={log.id} className="border-b border-slate-200 hover:bg-slate-100/40" data-testid={`email-row-${log.id}`}>
                        <td className="p-4">
                          <Checkbox
                            checked={selectedIds.includes(log.id)}
                            onCheckedChange={() => toggleSelect(log.id)}
                            data-testid={`select-email-${log.id}`}
                          />
                        </td>
                        <td className="p-4 text-sm text-slate-500 whitespace-nowrap">{formatDate(log.created_at)}</td>
                        <td className="p-4">
                          {log.scope === 'system' ? (
                            <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200">
                              <Globe className="w-3 h-3 mr-1" /> Sistema
                            </Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-500/30">
                              <Building2 className="w-3 h-3 mr-1" /> {log.tenant_name || 'Cliente'}
                            </Badge>
                          )}
                        </td>
                        <td className="p-4 text-sm text-slate-600">{log.to}</td>
                        <td className="p-4 text-sm text-slate-600 max-w-xs truncate" title={log.subject}>{log.subject}</td>
                        <td className="p-4">
                          <Badge className={`${meta.className} border`} title={log.error || ''}>
                            <StatusIcon className="w-3 h-3 mr-1" /> {meta.label}
                          </Badge>
                        </td>
                        <td className="p-4 text-right whitespace-nowrap">
                          <Button
                            variant="ghost" size="sm"
                            className="text-indigo-700 hover:bg-indigo-50 h-8"
                            onClick={() => openDetail(log)}
                            data-testid={`email-detail-${log.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {(log.events?.length || 0) > 0 ? `${log.events.length}` : 'Ver'}
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            className="text-red-600 hover:bg-red-50 h-8"
                            onClick={() => setDeleteTarget({ type: 'single', id: log.id })}
                            data-testid={`delete-email-${log.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Página {pagination.page} de {pagination.total_pages}</p>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              className="border-slate-300 text-slate-600"
              disabled={pagination.page <= 1}
              onClick={() => fetchLogs(pagination.page - 1)}
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
            </Button>
            <Button
              variant="outline" size="sm"
              className="border-slate-300 text-slate-600"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => fetchLogs(pagination.page + 1)}
            >
              Seguinte <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail dialog with delivery timeline */}
      <Dialog open={!!detailLog} onOpenChange={(o) => !o && setDetailLog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="email-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-700" /> Detalhe do Email
            </DialogTitle>
            <DialogDescription className="truncate">{detailLog?.subject}</DialogDescription>
          </DialogHeader>
          {detailLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Para</span><p className="font-medium text-slate-900">{detailLog.to}</p></div>
                <div><span className="text-slate-500">Remetente</span><p className="font-medium text-slate-900">{detailLog.sender_email}</p></div>
                <div><span className="text-slate-500">Âmbito</span><p className="font-medium text-slate-900">{detailLog.scope === 'system' ? 'Sistema' : (detailLog.tenant_name || 'Cliente')}</p></div>
                <div><span className="text-slate-500">Estado atual</span><div className="mt-1"><Badge className={`${statusMeta(detailLog.status).className} border`}>{statusMeta(detailLog.status).label}</Badge></div></div>
              </div>
              {detailLog.error && (
                <div className="text-sm bg-red-50 border border-red-200 rounded-md p-3 text-red-700">{detailLog.error}</div>
              )}
              <div>
                <p className="text-sm font-semibold text-slate-900 mb-2">Cronologia de entrega</p>
                {(detailLog.events?.length || 0) === 0 ? (
                  <p className="text-sm text-slate-500">
                    Sem eventos do SendGrid ainda. Assim que os webhooks estiverem ativos, aparecem aqui os estados "entregue", "aberto", etc.
                  </p>
                ) : (
                  <ol className="relative border-l border-slate-200 ml-2 space-y-3">
                    {detailLog.events.map((ev, i) => {
                      const m = statusMeta(ev.event);
                      const Ic = m.icon;
                      const ts = ev.timestamp ? new Date(ev.timestamp * 1000).toLocaleString('pt-PT') : '';
                      return (
                        <li key={ev.sg_event_id || i} className="ml-4">
                          <span className="absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full bg-indigo-600" />
                          <div className="flex items-center gap-2">
                            <Ic className="w-4 h-4 text-slate-500" />
                            <span className="text-sm font-medium text-slate-900">{m.label}</span>
                            <span className="text-xs text-slate-500">{ts}</span>
                          </div>
                          {ev.reason && <p className="text-xs text-red-600 mt-0.5">{ev.reason}</p>}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>

              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-900">Conteúdo enviado (pré-visualização)</p>
                  {detailLog.html_body && (
                    <Button
                      variant="ghost" size="sm"
                      className="text-indigo-700 hover:bg-indigo-50 h-7 text-xs"
                      onClick={() => setShowRawHtml(v => !v)}
                      data-testid="toggle-raw-html-btn"
                    >
                      {showRawHtml ? 'Ver pré-visualização' : 'Ver HTML bruto'}
                    </Button>
                  )}
                </div>

                {detailLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-indigo-700" />
                  </div>
                ) : !detailLog.html_body ? (
                  <p className="text-sm text-slate-500">
                    Sem conteúdo guardado para este email (enviado antes desta funcionalidade existir).
                  </p>
                ) : (
                  <>
                    {detailLog.links?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-slate-500 mb-1">Links encontrados no email</p>
                        <ul className="space-y-1" data-testid="email-links-list">
                          {detailLog.links.map((link) => (
                            <li key={link} className="text-xs">
                              <a href={link} target="_blank" rel="noopener noreferrer" className="text-indigo-700 hover:underline break-all">
                                {link}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {showRawHtml ? (
                      <pre className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3 max-h-72 overflow-auto whitespace-pre-wrap break-all" data-testid="email-raw-html">
                        {detailLog.html_body}
                      </pre>
                    ) : (
                      <div className="border border-slate-200 rounded-md overflow-hidden bg-slate-100">
                        <iframe
                          title="Pré-visualização do email"
                          srcDoc={detailLog.html_body}
                          sandbox=""
                          className="w-full bg-white"
                          style={{ height: 340 }}
                          data-testid="email-html-preview"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="delete-email-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" /> Eliminar registo(s) de email
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.type === 'bulk'
                ? `Tem a certeza que deseja eliminar ${deleteTarget.count} registo(s) selecionado(s)? Esta ação não pode ser desfeita.`
                : 'Tem a certeza que deseja eliminar este registo de email? Esta ação não pode ser desfeita.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="cancel-delete-email-log-btn">
              Cancelar
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="confirm-delete-email-log-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailLogsPage;
